import { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Grid, IconButton, Typography, Box,
  MenuItem, FormControlLabel, Switch, CircularProgress
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import CloseIcon from '@mui/icons-material/Close';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { postData, updateData, fetchData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';

const TYPE_OPTIONS = [
  { value: 1, label: 'Customer' },
  { value: 2, label: 'Supplier' },
];

const EMPTY_FORM = {
  Name: '', ContactNumber: '', Type: 1,
  AddressLine1: '', AddressLine2: '', GSTNumber: '',
  Remarks: '', OpeningBalance: '', IsPayable: false,
  OpeningBalance2: '', IsPayable2: false,
  IsAdminView: false,
  PostCode: '', PostalId: 0, PostHeadOffice: '',
  PostPlace: '', District: '', State: '', Country: '',
};

const MasterModal = ({ open, onClose, onSave, MasterData = null, mode = 'create', existingMasters = [] }) => {
  const { companyId, userId, token, roleName } = useAuth();
  const isAdminRole = roleName === 'CompanyAdmin' || roleName === 'SuperAdmin';
  const isCompany2 = Number(companyId) === 2;
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 600);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [duplicateMessage, setDuplicateMessage] = useState('');

  // Postal autocomplete state
  const [allPostalData, setAllPostalData] = useState([]);
  const [postalOptions, setPostalOptions] = useState([]);
  const [postalInputValue, setPostalInputValue] = useState('');
  const [postalLoading, setPostalLoading] = useState(false);
  const [selectedPostal, setSelectedPostal] = useState(null);

  // Load all postal data once
  useEffect(() => {
    if (open && allPostalData.length === 0) {
      setPostalLoading(true);
      fetchData('MasterApi', `/pincode/${companyId}`, token)
        .then(response => {
          if (response && response.responseCode === '1111' && response.response_data) {
            const data = Array.isArray(response.response_data) ? response.response_data : [response.response_data];
            setAllPostalData(data);
            setPostalOptions(data.slice(0, 50)); // seed initial options
          }
        })
        .catch(() => {})
        .finally(() => setPostalLoading(false));
    }
  }, [open]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 600);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (open) {
      if (MasterData && mode !== 'create') {
        setFormData({
          Id: MasterData.Id || '',
          Name: MasterData.Name || '',
          ContactNumber: MasterData.ContactNumber || '',
          Type: MasterData.Type ?? 1,
          AddressLine1: MasterData.AddressLine1 || '',
          AddressLine2: MasterData.AddressLine2 || '',
          GSTNumber: MasterData.GSTNumber || '',
          Remarks: MasterData.Remarks || '',
          OpeningBalance: MasterData.OpeningBalance ?? '',
          IsPayable: MasterData.IsPayable ?? false,
          OpeningBalance2: MasterData.OpeningBalance2 ?? '',
          IsPayable2: MasterData.IsPayable2 ?? false,
          IsAdminView: MasterData.IsAdminView ?? false,
          PostCode: MasterData.PostCode || '',
          PostalId: MasterData.PostalId || 0,
          PostHeadOffice: MasterData.PostHeadOffice || '',
          PostPlace: MasterData.PostPlace || '',
          District: MasterData.District || '',
          State: MasterData.State || '',
          Country: MasterData.Country || '',
        });
        // Pre-populate the postal input display
        if (MasterData.PostCode) {
          const preSelected = {
            Id: MasterData.PostalId,
            PinCode: MasterData.PostCode,
            PostHeadOffice: MasterData.PostHeadOffice || '',
            PostPlace: MasterData.PostPlace || '',
            District: MasterData.District || '',
            States: MasterData.State || '',
            Country: MasterData.Country || '',
          };
          setSelectedPostal(preSelected);
          setPostalInputValue(
            `${preSelected.PinCode}${preSelected.PostPlace ? ' – ' + preSelected.PostPlace : ''}${preSelected.District ? ' – ' + preSelected.District : ''}`
          );
        }
      } else {
        setFormData(EMPTY_FORM);
        setPostalInputValue('');
        setSelectedPostal(null);
        setPostalOptions([]);
      }
      setErrors({});
    }
  }, [open, MasterData, mode]);

  // Filter locally on input change
  const handlePostalInputChange = (_, val) => {
    setPostalInputValue(val);
    if (!val || val.length < 1) {
      // Show first 50 when empty so dropdown opens on focus
      setPostalOptions(allPostalData.slice(0, 50));
      return;
    }
    const q = val.toLowerCase();
    const filtered = allPostalData.filter(p =>
      p.PinCode?.toLowerCase().includes(q) ||
      p.PostPlace?.toLowerCase().includes(q) ||
      p.District?.toLowerCase().includes(q)
    ).slice(0, 50);
    setPostalOptions(filtered);
  };

  const handlePostalSelect = (_, selected) => {
    setSelectedPostal(selected);
    if (selected) {
      setFormData(prev => ({
        ...prev,
        PostCode: selected.PinCode || '',
        PostalId: selected.Id || 0,
        PostHeadOffice: selected.PostHeadOffice || '',
        PostPlace: selected.PostPlace || '',
        District: selected.District || '',
        State: selected.States || '',
        Country: selected.Country || '',
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        PostCode: '', PostalId: 0, PostHeadOffice: '',
        PostPlace: '', District: '', State: '', Country: '',
      }));
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.Name.trim()) newErrors.Name = 'Name is required';
    if (!formData.Type) newErrors.Type = 'Type is required';
    if (formData.OpeningBalance !== '' && isNaN(formData.OpeningBalance))
      newErrors.OpeningBalance = 'Enter a valid amount';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const checkDuplicate = () => existingMasters.find(m => {
    if (mode === 'edit' && MasterData && m.Id === MasterData.Id) return false;
    return m.Name?.trim().toLowerCase() === formData.Name.trim().toLowerCase()
      && m.Type === parseInt(formData.Type);
  });

  const handleSave = async () => {
    if (!validateForm()) return;
    const duplicate = checkDuplicate();
    if (duplicate) {
      setDuplicateMessage(`A ${TYPE_OPTIONS.find(t => t.value === parseInt(formData.Type))?.label} with the name "${formData.Name}" already exists.`);
      setDuplicateDialogOpen(true);
      return;
    }
    if (mode === 'edit' && (!MasterData || !MasterData.Id)) {
      alert('Error: Cannot update Master - missing ID');
      return;
    }
    setLoading(true);
    try {
      const dataToSend = {
        Name: formData.Name,
        ContactNumber: formData.ContactNumber || null,
        Type: parseInt(formData.Type),
        AddressLine1: formData.AddressLine1 || null,
        AddressLine2: formData.AddressLine2 || null,
        GSTNumber: formData.GSTNumber || null,
        Remarks: formData.Remarks || null,
        OpeningBalance: isCompany2 ? 0 : (parseFloat(formData.OpeningBalance) || 0),
        IsPayable: isCompany2 ? false : formData.IsPayable,
        OpeningBalance2: isCompany2 ? (parseFloat(formData.OpeningBalance2) || 0) : 0,
        IsPayable2: isCompany2 ? formData.IsPayable2 : false,
        IsAdminView: formData.IsAdminView,
        PostCode: formData.PostCode || null,
        PostalId: parseInt(formData.PostalId) || 0,
        PostHeadOffice: formData.PostHeadOffice || null,
        PostPlace: formData.PostPlace || null,
        District: formData.District || null,
        State: formData.State || null,
        Country: formData.Country || null,
        CompanyId: companyId,
      };
      if (mode === 'edit') {
        dataToSend.Id = MasterData.Id;
        dataToSend.ModifiedBy = userId;
        dataToSend.ModifiedDate = new Date();
        if (MasterData.CreatedBy) dataToSend.CreatedBy = MasterData.CreatedBy;
        if (MasterData.CreatedDate) dataToSend.CreatedDate = MasterData.CreatedDate;
        await updateData('MasterApi', MasterData.Id, dataToSend, token);
      } else {
        dataToSend.CreatedBy = userId;
        dataToSend.CreatedDate = new Date();
        await postData('MasterApi','', dataToSend, token);
      }
      onSave();
      onClose();
    } catch (error) {
      console.error('Error saving Master:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTitle = () => ({ create: 'New Master', edit: 'Edit Master', view: 'Master Details' }[mode] || 'Master');
  const isReadOnly = mode === 'view';
  const inputStyle = { borderRadius: '6px', fontSize: '13px', height: '32px' };
  const labelStyle = { fontSize: '11px' };
  const helperStyle = { fontSize: '9px', margin: '1px 0 0 0' };
  const autoFilledStyle = { ...inputStyle, backgroundColor: selectedPostal ? '#f0f7f0' : undefined };

  return (
    <>
      <Dialog
        open={open} onClose={onClose} maxWidth="md" fullWidth fullScreen={isMobile}
        PaperProps={{
          style: {
            borderRadius: isMobile ? '0' : '12px',
            background: 'linear-gradient(135deg, #3b2410 0%, #5c3d1e 100%)',
            padding: '2px', margin: isMobile ? '0' : '8px'
          }
        }}
      >
        <Box style={{ background: 'white', borderRadius: isMobile ? '0' : '10px', overflow: 'hidden', height: isMobile ? '100vh' : 'auto' }}>
          <DialogTitle style={{
            background: 'linear-gradient(135deg, #3b2410 0%, #5c3d1e 100%)',
            color: 'white', padding: '8px 12px',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', minHeight: 'auto'
          }}>
            <Box style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <PeopleAltIcon style={{ fontSize: '18px' }} />
              <Typography variant="h6" style={{ fontWeight: '600', fontSize: '14px' }}>{getTitle()}</Typography>
            </Box>
            <IconButton onClick={onClose} style={{ color: 'white', padding: '2px' }} size="small">
              <CloseIcon style={{ fontSize: '18px' }} />
            </IconButton>
          </DialogTitle>

          <DialogContent style={{ padding: '8px 12px', paddingBottom: '4px' }}>
            <Typography variant="subtitle2" style={{ color: '#666', marginBottom: '6px', fontSize: '10px', lineHeight: '1.2' }}>
              {mode === 'create' ? 'Add a new Master to your system' : mode === 'edit' ? 'Update Master information' : 'View Master details'}
            </Typography>

            <Grid container spacing={1}>

              {/* ── Basic Info ── */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" style={{ fontWeight: '600', marginBottom: '3px', color: '#2d3748', fontSize: '12px' }}>
                  Basic Information
                </Typography>
              </Grid>
              <Grid item xs={12} sm={5}>
                <TextField
                  name="Name" label="Name *" value={formData.Name} onChange={handleChange}
                  fullWidth size="small" error={!!errors.Name} helperText={errors.Name}
                  disabled={isReadOnly} placeholder="Enter name" inputProps={{ maxLength: 200 }}
                  InputProps={{ style: inputStyle }} InputLabelProps={{ style: labelStyle }}
                  FormHelperTextProps={{ style: helperStyle }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  name="ContactNumber" label="Contact Number" value={formData.ContactNumber} onChange={handleChange}
                  fullWidth size="small" disabled={isReadOnly} placeholder="Enter contact number"
                  inputProps={{ maxLength: 15 }}
                  InputProps={{ style: inputStyle }} InputLabelProps={{ style: labelStyle }}
                  FormHelperTextProps={{ style: helperStyle }}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  select name="Type" label="Type *" value={formData.Type} onChange={handleChange}
                  fullWidth size="small" error={!!errors.Type} helperText={errors.Type}
                  disabled={isReadOnly}
                  InputProps={{ style: { ...inputStyle, padding: 0 } }} InputLabelProps={{ style: labelStyle }}
                  FormHelperTextProps={{ style: helperStyle }}
                >
                  {TYPE_OPTIONS.map(opt => (
                    <MenuItem key={opt.value} value={opt.value} style={{ fontSize: '13px' }}>{opt.label}</MenuItem>
                  ))}
                </TextField>
              </Grid>

              {/* ── Address ── */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" style={{ fontWeight: '600', marginBottom: '3px', color: '#2d3748', fontSize: '12px', marginTop: '4px' }}>
                  Address
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="AddressLine1" label="Address Line 1" value={formData.AddressLine1} onChange={handleChange}
                  fullWidth size="small" disabled={isReadOnly} placeholder="Street / Area"
                  inputProps={{ maxLength: 150 }}
                  InputProps={{ style: inputStyle }} InputLabelProps={{ style: labelStyle }}
                  FormHelperTextProps={{ style: helperStyle }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="AddressLine2" label="Address Line 2" value={formData.AddressLine2} onChange={handleChange}
                  fullWidth size="small" disabled={isReadOnly} placeholder="City / State / PIN"
                  inputProps={{ maxLength: 150 }}
                  InputProps={{ style: inputStyle }} InputLabelProps={{ style: labelStyle }}
                  FormHelperTextProps={{ style: helperStyle }}
                />
              </Grid>

              {/* ── Postal Details ── */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" style={{ fontWeight: '600', marginBottom: '3px', color: '#2d3748', fontSize: '12px', marginTop: '4px' }}>
                  Postal Details
                </Typography>
              </Grid>

              {/* PostCode Autocomplete dropdown */}
              <Grid item xs={12} sm={4}>
                <Autocomplete
                  disabled={isReadOnly}
                  options={postalOptions}
                  value={selectedPostal}
                  inputValue={postalInputValue}
                  onInputChange={handlePostalInputChange}
                  onChange={handlePostalSelect}
                  getOptionLabel={(opt) =>
                    opt ? `${opt.PinCode || ''}${opt.PostPlace ? ' – ' + opt.PostPlace : ''}${opt.District ? ' – ' + opt.District : ''}` : ''
                  }
                  isOptionEqualToValue={(opt, val) => opt.Id === val?.Id}
                  loading={postalLoading}
                  filterOptions={(x) => x}
                  noOptionsText={postalLoading ? 'Loading...' : 'No results'}
                  renderOption={(props, opt) => (
                    <li {...props} key={opt.Id} style={{ fontSize: '12px', padding: '6px 12px', borderBottom: '1px solid #f0f0f0' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontWeight: '700', color: '#5c3d1e', minWidth: '52px', fontSize: '12px' }}>{opt.PinCode}</span>
                        <span style={{ fontWeight: '600', color: '#2d3748', fontSize: '12px' }}>{opt.PostPlace}</span>
                        {opt.District && <span style={{ color: '#888', fontSize: '11px' }}>{opt.District}</span>}
                      </div>
                      {(opt.States || opt.PostHeadOffice || opt.BranchOffice) && (
                        <div style={{ fontSize: '10px', color: '#aaa', marginTop: '1px', paddingLeft: '60px' }}>
                          {[opt.PostHeadOffice, opt.BranchOffice, opt.States].filter(Boolean).join(', ')}
                        </div>
                      )}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Post Code"
                      size="small"
                      placeholder="Type to search..."
                      error={!!errors.PostCode}
                      helperText={errors.PostCode}
                      InputProps={{
                        ...params.InputProps,
                        style: inputStyle,
                        endAdornment: (
                          <>
                            {postalLoading && <CircularProgress size={12} style={{ color: '#5c3d1e' }} />}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                      InputLabelProps={{ style: labelStyle }}
                      FormHelperTextProps={{ style: helperStyle }}
                    />
                  )}
                  sx={{
                    '& .MuiOutlinedInput-root': { borderRadius: '6px', fontSize: '13px', height: '32px', padding: '0 8px !important' },
                    '& .MuiAutocomplete-endAdornment': { top: '4px' },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  name="PostHeadOffice" label="Head Office" value={formData.PostHeadOffice}
                  onChange={handleChange} fullWidth size="small"
                  disabled={isReadOnly} placeholder="Auto-filled on selection"
                  inputProps={{ maxLength: 50 }}
                  InputProps={{ style: autoFilledStyle }} InputLabelProps={{ style: labelStyle }}
                  FormHelperTextProps={{ style: helperStyle }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  name="PostPlace" label="Post Place" value={formData.PostPlace}
                  onChange={handleChange} fullWidth size="small"
                  disabled={isReadOnly} placeholder="Auto-filled on selection"
                  inputProps={{ maxLength: 50 }}
                  InputProps={{ style: autoFilledStyle }} InputLabelProps={{ style: labelStyle }}
                  FormHelperTextProps={{ style: helperStyle }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  name="District" label="District" value={formData.District}
                  onChange={handleChange} fullWidth size="small"
                  disabled={isReadOnly} placeholder="Auto-filled on selection"
                  inputProps={{ maxLength: 50 }}
                  InputProps={{ style: autoFilledStyle }} InputLabelProps={{ style: labelStyle }}
                  FormHelperTextProps={{ style: helperStyle }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  name="State" label="State" value={formData.State}
                  onChange={handleChange} fullWidth size="small"
                  disabled={isReadOnly} placeholder="Auto-filled on selection"
                  inputProps={{ maxLength: 50 }}
                  InputProps={{ style: autoFilledStyle }} InputLabelProps={{ style: labelStyle }}
                  FormHelperTextProps={{ style: helperStyle }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  name="Country" label="Country" value={formData.Country}
                  onChange={handleChange} fullWidth size="small"
                  disabled={isReadOnly} placeholder="Auto-filled on selection"
                  inputProps={{ maxLength: 50 }}
                  InputProps={{ style: autoFilledStyle }} InputLabelProps={{ style: labelStyle }}
                  FormHelperTextProps={{ style: helperStyle }}
                />
              </Grid>

              {/* ── Financial ── */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" style={{ fontWeight: '600', marginBottom: '3px', color: '#2d3748', fontSize: '12px', marginTop: '4px' }}>
                  Financial Details
                </Typography>
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  name="GSTNumber" label="GST Number" value={formData.GSTNumber} onChange={handleChange}
                  fullWidth size="small" disabled={isReadOnly} placeholder="Enter GST number"
                  inputProps={{ maxLength: 15 }}
                  InputProps={{ style: inputStyle }} InputLabelProps={{ style: labelStyle }}
                  FormHelperTextProps={{ style: helperStyle }}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                {isCompany2 ? (
                  <TextField
                    name="OpeningBalance2" label="Opening Balance (₹)" value={formData.OpeningBalance2} onChange={handleChange}
                    fullWidth size="small" type="number" error={!!errors.OpeningBalance2} helperText={errors.OpeningBalance2}
                    disabled={isReadOnly} placeholder="0.00" inputProps={{ step: '0.01' }}
                    InputProps={{ style: inputStyle }} InputLabelProps={{ style: labelStyle }}
                    FormHelperTextProps={{ style: helperStyle }}
                  />
                ) : (
                  <TextField
                    name="OpeningBalance" label="Opening Balance (₹)" value={formData.OpeningBalance} onChange={handleChange}
                    fullWidth size="small" type="number" error={!!errors.OpeningBalance} helperText={errors.OpeningBalance}
                    disabled={isReadOnly} placeholder="0.00" inputProps={{ step: '0.01' }}
                    InputProps={{ style: inputStyle }} InputLabelProps={{ style: labelStyle }}
                    FormHelperTextProps={{ style: helperStyle }}
                  />
                )}
              </Grid>
              <Grid item xs={6} sm={3} style={{ display: 'flex', alignItems: 'center' }}>
                {isCompany2 ? (
                  <FormControlLabel
                    control={
                      <Switch name="IsPayable2" checked={formData.IsPayable2} onChange={handleChange}
                        disabled={isReadOnly} size="small"
                        sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#5c3d1e' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#5c3d1e' } }}
                      />
                    }
                    label={<Typography style={{ fontSize: '12px', color: '#2d3748' }}>Is Payable</Typography>}
                  />
                ) : (
                  <FormControlLabel
                    control={
                      <Switch name="IsPayable" checked={formData.IsPayable} onChange={handleChange}
                        disabled={isReadOnly} size="small"
                        sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#5c3d1e' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#5c3d1e' } }}
                      />
                    }
                    label={<Typography style={{ fontSize: '12px', color: '#2d3748' }}>Is Payable</Typography>}
                  />
                )}
              </Grid>
              <Grid item xs={6} sm={3} style={{ display: 'flex', alignItems: 'center' }}>
                <FormControlLabel
                  control={
                    <Switch
                      name="IsAdminView"
                      checked={formData.IsAdminView}
                      onChange={handleChange}
                      disabled={isReadOnly || !isAdminRole}
                      size="small"
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': { color: '#5c3d1e' },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#5c3d1e' },
                      }}
                    />
                  }
                  label={<Typography style={{ fontSize: '12px', color: '#2d3748' }}>Is Admin View</Typography>}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  name="Remarks" label="Remarks" value={formData.Remarks} onChange={handleChange}
                  fullWidth size="small" disabled={isReadOnly} placeholder="Any additional notes"
                  inputProps={{ maxLength: 200 }} multiline minRows={2}
                  InputLabelProps={{ style: labelStyle }}
                  FormHelperTextProps={{ style: helperStyle }}
                />
              </Grid>

            </Grid>
          </DialogContent>

          <DialogActions style={{ padding: '8px 12px', background: '#f8f9fa', borderTop: '1px solid #e9ecef', gap: '6px', minHeight: 'auto' }}>
            <Button onClick={onClose} style={{ color: '#6c757d', textTransform: 'none', fontWeight: '500', fontSize: '12px', padding: '4px 8px' }}>
              Cancel
            </Button>
            {!isReadOnly && (
              <Button onClick={handleSave} disabled={loading} variant="contained"
                style={{ background: 'linear-gradient(135deg, #c9a84c 0%, #9a7a2e 100%)', color: 'white', textTransform: 'none', fontWeight: '600', borderRadius: '4px', padding: '4px 12px', fontSize: '12px' }}>
                {loading ? 'Saving...' : mode === 'create' ? 'Add Master' : 'Update Master'}
              </Button>
            )}
          </DialogActions>
        </Box>
      </Dialog>

      <Dialog open={duplicateDialogOpen} onClose={() => setDuplicateDialogOpen(false)} maxWidth="xs" fullWidth
        PaperProps={{ style: { borderRadius: '12px', padding: '8px' } }}>
        <DialogContent style={{ textAlign: 'center', padding: '24px' }}>
          <WarningAmberIcon style={{ fontSize: '64px', color: '#ff9800', marginBottom: '16px' }} />
          <Typography variant="h6" style={{ fontWeight: '600', marginBottom: '12px', color: '#2d3748', fontSize: '16px' }}>
            Duplicate Master Found
          </Typography>
          <Typography style={{ color: '#666', fontSize: '14px', lineHeight: '1.5' }}>{duplicateMessage}</Typography>
        </DialogContent>
        <DialogActions style={{ padding: '0 24px 24px 24px', justifyContent: 'center' }}>
          <Button onClick={() => setDuplicateDialogOpen(false)} variant="contained"
            style={{ background: 'linear-gradient(135deg, #5c3d1e 0%, #7a5230 100%)', color: 'white', textTransform: 'none', fontWeight: '600', borderRadius: '8px', padding: '8px 32px', fontSize: '14px' }}>
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default MasterModal;
